import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import lodashUniq from 'lodash/fp/uniq';
import lodashCompact from 'lodash/fp/compact';

import {
  SR_API_ACTION_ADMIN_CREATE_USER_REQUEST_TOUR,
} from 'api/actionTypes';

import { USER_ROLE_ADMIN } from 'common/userRoles';
import { leaseSituationShortOptionItems } from 'common/leaseSituationOptionItems';
import getApplicationFormFields, {
  APPLICATION_FORM_TYPE_RENTER_PREQUAL,
  renterPrequalFieldgroup,
} from 'common/householdApplication/applicationFormFields';

import Navbar from 'client/components/Navbar';
import SrFormikField from 'client/components/SrFormikField';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import allowedRoles from 'client/helpers/allowedRoles';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';
import leaseTermOptionItems from 'client/helpers/leaseTermOptionItems';
import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';
import postWithState from 'client/actions/postWithState';
import {
  SCREEN_ID_ADMIN_USER_TOUR_CREATOR,
} from 'client/constants/screenIds';

export const adminCreateUserFormFields = [
  {
    id: 'name',
    title: 'Name',
    type: 'short_text',
    properties: {
      placeholder: '',
    },
    validations: ['required'],
  },
  {
    id: 'email',
    title: 'Email',
    type: 'short_text',
    properties: {
      placeholder: '',
    },
    validations: ['required', 'email'],
  },
  {
    id: 'phoneNumber',
    title: 'Phone',
    type: 'phone',
    properties: {
      placeholder: '',
    },
    validations: ['required', 'phone'],
  },
  {
    id: 'moveInDate',
    title: 'move-in date',
    type: 'date_picker',
    properties: {
      placeholder: 'Choose date...',
    },
    validations: ['required', 'date', 'noPastDates'],
  },
  {
    id:'minRentPrice',
    title:'property price min',
    type: 'currency',
    properties: {
      placeholder: '$0',
    },
    validations: ['required'],
  },
  {
    id:'maxRentPrice',
    title:'property price max',
    type: 'currency',
    properties: {
      placeholder: '$2,000',
    },
    validations: ['required'],
  },
  {
    id: 'leaseSituation',
    title: 'lease situation',
    type: 'select',
    properties: {
      choices: leaseSituationShortOptionItems,
      multipleSelection: false,
      allowEmptyArray: false,
    },
    validations: ['required'],
  },
  {
    id: 'leaseTerm',
    title: 'lease term',
    type: 'select',
    properties: {
      choices: leaseTermOptionItems,
      multipleSelection: false,
      allowEmptyArray: false,
    },
    validations: ['required'],
  },
];

const adminCreateTourFormFields = [
  {
    id: 'mlsNumber',
    title: 'mls # | listing id',
    type: 'short_text',
    properties: {
    },
    validations: ['required'],
  },
  {
    id: 'startDate',
    title: 'tour date',
    type: 'date_picker',
    properties: {
      timeFormat: true,
      readOnly: false,
    },
    validations: ['required', 'date', 'noPastDates'],
  },
];

const renterPrequalRootFieldgroup = lodashUniq(renterPrequalFieldgroup.map((field) => field.split('.')[0]));
const getPrequalApplicationValues = (values) => {
  return renterPrequalRootFieldgroup.reduce((memo, prequalField) => {
    if (
      typeof values[prequalField] !== 'undefined'
      && values[prequalField] !== ''
    ) {
      memo[prequalField] = values[prequalField];
    }
    return memo;
  }, {});
};

const blacklistValidation = ['required', 'creditScore'];
const upateApplicationFormFieldConfiguration = (formField, formikBag) => {
  const newFormFieldConfiguration = {
    ...formField,
    label: null,
    validations: (formField.validations || [])
      .filter((validation) => !blacklistValidation.includes(validation)),
  };

  if (
    formField.type === 'multiple_choice'
    && formField.properties
    && !formField.properties.multipleSelection
  ) {
    newFormFieldConfiguration.type = 'select';
    newFormFieldConfiguration.properties = {
      choices: formField.properties.choices,
      enableResetOption: true,
      onChange: (e) => {
        formikBag.setFieldValue(
          formField.id,
          isNaN(e.target.value) ? e.target.value : parseInt(e.target.value, 10)
        );
      },
    };
  }

  if (formField.type === 'yes_no') {
    newFormFieldConfiguration.type = 'select';
    newFormFieldConfiguration.properties = {
      choices: [
        { value: true, label: 'Yes' }, { value: false, label: 'No' },
      ],
      enableResetOption: true,
      onChange: (e) => {
        if (!e.target.value) {
          formikBag.setFieldValue(formField.id, undefined);
        }
        else {
          formikBag.setFieldValue(formField.id, e.target.value === 'true');
        }
      },
    };
  }

  return newFormFieldConfiguration;
};

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  createUserRequestTourApiState: {
    action: SR_API_ACTION_ADMIN_CREATE_USER_REQUEST_TOUR,
  },
})
@branch()
@withScreenId(SCREEN_ID_ADMIN_USER_TOUR_CREATOR)
class AdminUserTourCreatorPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Admin User/Tour Creator | Sunroom Rentals',
    section: 'Admin',
  });

  static propTypes = {
    createUserRequestTourApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
  }

  _onSubmitForm = (values, formikBag) => {
    const { dispatch } = this.props;

    const application = getPrequalApplicationValues(values);

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_CREATE_USER_REQUEST_TOUR,
      payload: {
        user: {
          name: values.name,
          password: `${Date.now()}`,
          phone: values.phoneNumber,
          email: values.email,
          signupProvider: 'adminTourRequest',
          leaseTerm: values.leaseTerm,
          leaseSituation: values.leaseSituation,
          moveInDate: values.moveInDate,
          searchFilters: {
            minRentPrice: values.minRentPrice,
            maxRentPrice: values.maxRentPrice,
            propertyTypes: [],
            petCount: application.petCount || 0,
            pets: lodashUniq(lodashCompact((application.pets || []).map((pet) => pet.type))),
            moveInDate: values.moveInDate,
            availableHomesNearMoveInDate: false,
            section8: false,
          },
        },
        tour: {
          mlsNumber: values.mlsNumber,
          startDate: values.startDate,
        },
        application,
      },
      onSuccess: () => {
        formikBag.resetForm();
        if (typeof global.alert === 'function') {
          global.alert('User and tour created!');
        }
      },
    });
  }

  render() {
    const {
      createUserRequestTourApiState,
    } = this.props;
    const formFields = [
      ...adminCreateUserFormFields,
      ...adminCreateTourFormFields,
      ...getApplicationFormFields({
        formValues: {},
        roommates: [],
        formType: APPLICATION_FORM_TYPE_RENTER_PREQUAL,
      }).map((formField) => upateApplicationFormFieldConfiguration(formField)),
    ];

    return (
      <div className='page-admin SrAdminTourCreatorPage'>
        <div className='adminContainer'>
          <Navbar redirectLogoTo='/admin' />
          <Formik
            enableReinitialize
            validateOnChange
            initialValues={createInitialValues({
              formFields: formFields,
              defaultValues: {},
            })}
            onSubmit={this._onSubmitForm}
            validate={createFormValidationFn(formFields)}
            render={(formikBag) => {
              const applicationFormFields = getApplicationFormFields({
                formValues: formikBag.values,
                roommates: [],
                formType: APPLICATION_FORM_TYPE_RENTER_PREQUAL,
              }).map((formField) => upateApplicationFormFieldConfiguration(formField, formikBag));

              return (
                <div>
                  <div>
                    <span className='formTitle'>user information (required)</span>
                    <div className='forms'>
                      {adminCreateUserFormFields.map((formField) => (
                        <div key={formField.id}>
                          <SrFormikField
                            formField={formField}
                            formikBag={formikBag}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span className='formTitle'>tour (required)</span>
                    <div className='forms'>
                      {adminCreateTourFormFields.map((formField) => (
                        <div key={formField.id}>
                          <SrFormikField
                            formField={formField}
                            formikBag={formikBag}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span className='formTitle'>prequal (optional)</span>
                    <div className='forms prequal'>
                      {applicationFormFields.map((formField) => (
                        <div key={formField.id}>
                          <SrFormikField
                            formField={formField}
                            formikBag={formikBag}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {createUserRequestTourApiState.error && (
                    <div className='apiStateError' style={{ color: 'red' }}>
                      {createUserRequestTourApiState.error.message}
                    </div>
                  )}

                  <ButtonDeprecated
                    onClick={formikBag.handleSubmit}
                    disabled={!formikBag.isValid || createUserRequestTourApiState.inProgress}
                  >
                    {createUserRequestTourApiState.inProgress ? 'Creating ...' : 'Create'}
                  </ButtonDeprecated>
                </div>
              );
            }}
          />
        </div>
      </div>
    );
  }
}

AdminUserTourCreatorPage.displayName = 'AdminUserTourCreatorPage';

export default AdminUserTourCreatorPage;
